// src/frontend/pages/FiveClassesViewer.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

const TITLE_BLUE = "#175cd3";
const SIDEBAR_BG = "#f7f9fb";
const SIDEBAR_BORDER = "#e6ebf1";

const BASE_PATH = "/course/clases";

// 👉 Aquí pones la URL de tu imagen de portada desde GitHub
// (cámbiala por la tuya)
const DEFAULT_OVERLAY_IMAGE =
  "https://raw.githubusercontent.com/tu-usuario/tu-repo/main/ruta/a/tu-imagen.png";

// Solo cambié esta parte: ahora usamos youtubeId
const classesData = [
  {
    id: 1,
    title: "Clase 1-Introducción",
    youtubeId: "XBktHWP8TLA", // <-- ID del video de YouTube
    overlayImage: DEFAULT_OVERLAY_IMAGE, // opcional, puedes poner una distinta por clase
    materials: [
      {
        label: "Historia del dinero (lectura complementaria)",
        to: "/course/historia-del-dinero",
      },
    ],
  },
  {
    id: 2,
    title:
      "Clase 2-Fundamentos del Bitcoin: Dinero digital, escasez y descentralización",
    youtubeId: "hcGkZGrjWpA",
    overlayImage: DEFAULT_OVERLAY_IMAGE,
    materials: [
      {
        label: "Blockchain en la Industria",
        to: "/course/blockchain-en-la-industria",
      },
      {
        label: "Blockchain Esencial",
        to: "/course/blockchain-fundamentos-esenciales/1",
      },
    ],
  },
  {
    id: 3,
    title: "Clase 3-Usos reales de la tecnología Blockchain",
    youtubeId: "VIDEO_ID_3",
    overlayImage: DEFAULT_OVERLAY_IMAGE,
    materials: [
      {
        label: "Explorando Hash y SHA-256",
        to: "/course/blockchain-en-la-industria",
      },
      {
        label: "Blockchain Esencial",
        to: "/course/blockchain-fundamentos-esenciales/1",
      },
      {
        label: "Curso Dominando el Blockchain: Día 1-7",
        to: "/course/CDB",
      },
    ],
  },
  {
    id: 4,
    title: "Clase 4-El BITCOIN",
    youtubeId: "yrItKXnWJnk",
    overlayImage: DEFAULT_OVERLAY_IMAGE,
    materials: [
      {
        label: "Bitcoin un antes y un después en la historia del dinero",
        to: "/course/bitcoin-historia",
      },
      {
        label: "Minería del Blockchain",
        to: "/course/mineria-blockchain",
      },
      {
        label: "Criptografía en Blockchain",
        to: "/course/criptografia-blockchain",
      },
      {
        label: "Bitcoin: De Cero a Avanzado",
        to: "/course/bitcoin-master/1",
      },
      {
        label: "Curso Dominando el Blockchain: Día 1-7",
        to: "/course/CDB",
      },
    ],
  },
];

// función para construir el embed de YouTube “limpio”
const getYouTubeEmbedUrl = (youtubeId, autoplay = false) => {
  if (!youtubeId) return "";
  const params = new URLSearchParams({
    rel: "0", // no mostrar videos relacionados de otros canales
    modestbranding: "1", // reduce el branding de YouTube
    controls: "0", // 0 = ocultar controles (tú pones tu propio botón)
    fs: "1", // permitir pantalla completa
    iv_load_policy: "3", // oculta anotaciones / info overlays
    playsinline: "1",
    autoplay: autoplay ? "1" : "0",
  }).toString();

  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params}`;
};

export default function FiveClassesViewer() {
  const { classId } = useParams();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(() => {
    return classesData.find((c) => c.id === Number(classId)) || classesData[0];
  });

  const [activeTab, setActiveTab] = useState("video"); // "video" | "study"
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // 👉 estado para manejar si el video está reproduciéndose
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const found = classesData.find((c) => c.id === Number(classId));
    if (found) {
      setSelected(found);
      setActiveTab("video"); // cuando cambias de clase, vuelves al video
      setIsPlaying(false); // resetea el estado de reproducción
    }
  }, [classId]);

  // si cambias de pestaña, resetea el play
  useEffect(() => {
    if (activeTab !== "video") {
      setIsPlaying(false);
    }
  }, [activeTab]);

  const handleSelect = (cls) => navigate(`${BASE_PATH}/${cls.id}`);

  const handleMaterialClick = (to) => {
    if (/^https?:\/\//i.test(to)) {
      window.open(to, "_blank", "noopener,noreferrer");
    } else {
      navigate(to);
    }
  };

  // construimos el SRC del video, con autoplay según isPlaying
  const youtubeSrc = selected?.youtubeId
    ? getYouTubeEmbedUrl(selected.youtubeId, isPlaying)
    : "";

  // al hacer clic en la imagen/overlay
  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  const overlayImage =
    selected?.overlayImage && selected.overlayImage.trim() !== ""
      ? selected.overlayImage
      : DEFAULT_OVERLAY_IMAGE;

  return (
    <div className="vv-serif-root vv-wrap">
      <style>{`
        :root { --header-offset: var(--header-offset, 0px); }

        .vv-serif-root, .vv-serif-root * {
          font-family: Georgia, "Times New Roman", Times, serif;
          letter-spacing: .01em;
        }

        /* Layout general: altura completa y solo el viewer se desplaza */
        .vv-wrap {
          display: flex;
          height: calc(100dvh - var(--header-offset));
          background: #fff;
          overflow: hidden;
        }

        /* Sidebar */
        .vv-sidebar {
          width: 280px;
          background: ${SIDEBAR_BG};
          border-right: 1px solid ${SIDEBAR_BORDER};
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .vv-title {
          background: ${TITLE_BLUE};
          color: #fff;
          font-weight: 800;
          font-size: 15px;
          padding: 14px 12px;
          margin: 0 0 8px 0;
          text-align: left;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .vv-titleText {
          flex: 1;
        }

        /* Botón para comprimir/expandir la lista de clases */
        .vv-toggleBtn {
          all: unset;
          cursor: pointer;
          font-size: 14px;
          line-height: 1;
          padding: 4px 6px;
          border-radius: 6px;
          border: 1px solid rgba(255,255,255,0.6);
        }
        .vv-toggleBtn:hover {
          background: rgba(15,23,42,0.25);
        }

        .vv-subtitle {
          color: #102a43;
          font-weight: 700;
          font-size: 13px;
          padding: 0 12px 8px 12px;
        }

        .vv-sidebarScroll {
          flex: 1;
          overflow-y: auto;
        }

        .vv-items { 
          list-style: none; 
          margin: 0; 
          padding: 0 6px 12px 6px; 
        }

        .vv-itemBtn {
          all: unset;
          display: grid;
          grid-template-columns: 12px 1fr auto;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 8px 10px 8px 6px;
          margin: 2px 4px;
          color: #243b53;
          cursor: pointer;
          text-align: left;
        }
        .vv-itemBtn:hover { background: #ffffff; }

        .vv-itemLabel {
          font-size: 13px; 
          line-height: 1.25;
          white-space: normal;
          overflow: visible;
          text-overflow: unset;
        }

        .vv-itemActive .vv-itemLabel { 
          color: ${TITLE_BLUE}; 
          font-weight: 800; 
        }

        .vv-circle {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          border: 2px solid #9ca3af;
        }
        .vv-itemActive .vv-circle {
          border-color: ${TITLE_BLUE};
          background: ${TITLE_BLUE};
        }

        .vv-sidebarCollapsed .vv-sidebarScroll {
          display: none;
        }

        .vv-viewer {
          flex: 1;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 16px clamp(12px, 2vw, 24px);
          margin-top: var(--header-offset);
          overflow-y: auto;
        }

        .vv-frameWrap { 
          width: 95%; 
          max-width: 960px; 
          margin: 0 auto; 
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .vv-classTitle {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 4px 0;
        }

        .vv-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #1f2937;
          padding-bottom: 0;
          margin-bottom: 8px;
        }
        .vv-tabBtn {
          all: unset;
          cursor: pointer;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px 8px 0 0;
          color: #9ca3af;
        }
        .vv-tabBtnActive {
          background: #111827;
          color: #f9fafb;
        }

        /* CONTENEDOR DE VIDEO + OVERLAY */
        .vv-videoShell {
          width: 100%;
        }

        .vv-videoInner {
          position: relative;
          width: 100%;
          max-width: 100%;
          height: 520px;
          overflow: hidden;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(15,23,42,0.18);
          background: #000;
        }

        .vv-iframe {
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
        }

        /* Overlay con imagen y botón "Reproducir video" */
        .vv-videoOverlayBtn {
          all: unset;
          position: absolute;
          inset: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .vv-videoOverlayImg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
        }

        .vv-videoOverlayGradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(15,23,42,0.75),
            rgba(15,23,42,0.25)
          );
          pointer-events: none;
        }

        .vv-videoOverlayContent {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          text-align: center;
          padding: 16px;
        }

        .vv-overlayPlayIcon {
          width: 56px;
          height: 56px;
          border-radius: 999px;
          border: 2px solid rgba(249,250,251,0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          line-height: 1;
          color: #f9fafb;
          box-shadow: 0 0 0 4px rgba(15,23,42,0.3);
          background: rgba(15,23,42,0.65);
        }

        .vv-overlayPlayText {
          font-size: 16px;
          font-weight: 700;
          color: #e5e7eb;
          text-shadow: 0 1px 3px rgba(15,23,42,0.9);
        }

        .vv-overlaySubtitle {
          font-size: 13px;
          color: #e5e7eb;
          max-width: 360px;
        }

        .vv-videoOverlayBtn:hover .vv-overlayPlayIcon {
          transform: scale(1.03);
        }

        /* Escudo para bloquear clics sobre YouTube (logo, título, etc.) */
        .vv-clickShield {
          position: absolute;
          inset: 0;
          pointer-events: auto;
          background: transparent;
        }

        .vv-materials {
          border: 1px solid ${SIDEBAR_BORDER};
          border-radius: 10px;
          padding: 12px 14px;
          background: #f9fafb;
        }
        .vv-materialsTitle {
          margin: 0 0 8px 0;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
        }
        .vv-materialsList {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .vv-materialItemBtn {
          all: unset;
          cursor: pointer;
          font-size: 13px;
          color: ${TITLE_BLUE};
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 6px;
          border-radius: 6px;
        }
        .vv-materialItemBtn:hover {
          background: #e0edff;
        }
        .vv-materialBullet {
          width: 4px;
          height: 4px;
          border-radius: 999px;
          background: ${TITLE_BLUE};
        }

        @media (max-width: 900px) {
          .vv-sidebar { width: 240px; }
          .vv-videoInner { height: 420px; }
        }
        @media (max-width: 720px) {
          .vv-wrap { 
            flex-direction: column;
            height: auto;
            overflow: visible;
          }
          .vv-sidebar {
            width: 100%;
            height: auto;
            border-right: none;
            border-bottom: 1px solid ${SIDEBAR_BORDER};
          }
          .vv-viewer { 
            margin-top: 0; 
            overflow-y: visible;
          }
          .vv-videoInner { height: 360px; }
        }
      `}</style>

      {/* Sidebar */}
      <aside
        className={`vv-sidebar ${
          sidebarCollapsed ? "vv-sidebarCollapsed" : ""
        }`}
        aria-label="Navegación de Clases"
      >
        <h1 className="vv-title">
          <span className="vv-titleText">Dominando Blockchain</span>
          <button
            type="button"
            className="vv-toggleBtn"
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={
              sidebarCollapsed
                ? "Mostrar lista de clases"
                : "Ocultar lista de clases"
            }
          >
            {sidebarCollapsed ? "▾" : "▴"}
          </button>
        </h1>

        <div className="vv-sidebarScroll">
          <div className="vv-subtitle">CLASES</div>

          <ul className="vv-items">
            {classesData.map((cls) => {
              const active = selected.id === cls.id;
              return (
                <li key={cls.id}>
                  <button
                    className={`vv-itemBtn ${
                      active ? "vv-itemActive" : ""
                    }`}
                    onClick={() => handleSelect(cls)}
                    aria-current={active ? "page" : undefined}
                  >
                    <span aria-hidden="true" />
                    <span className="vv-itemLabel">{cls.title}</span>
                    <span className="vv-circle" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      {/* Video + pestañas + guía de estudio */}
      <main className="vv-viewer">
        <div className="vv-frameWrap">
          {/* título de la clase */}
          <h1 className="vv-classTitle">{selected?.title}</h1>

          {/* pestañas tipo Bootstrap */}
          <nav className="vv-tabs" aria-label="Tipo de recurso">
            <button
              type="button"
              className={`vv-tabBtn ${
                activeTab === "video" ? "vv-tabBtnActive" : ""
              }`}
              onClick={() => setActiveTab("video")}
              aria-selected={activeTab === "video"}
            >
              Video
            </button>
            <button
              type="button"
              className={`vv-tabBtn ${
                activeTab === "study" ? "vv-tabBtnActive" : ""
              }`}
              onClick={() => setActiveTab("study")}
              aria-selected={activeTab === "study"}
            >
              Guía de estudio
            </button>
          </nav>

          {/* contenido según pestaña */}
          {activeTab === "video" ? (
            youtubeSrc ? (
              <div className="vv-videoShell">
                <div className="vv-videoInner">
                  <iframe
                    key={selected.id}
                    src={youtubeSrc}
                    className="vv-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title={selected.title}
                  />
                  {/* Overlay con imagen y botón mientras NO está reproduciendo */}
                  {!isPlaying && (
                    <button
                      type="button"
                      className="vv-videoOverlayBtn"
                      onClick={handlePlayClick}
                      aria-label="Reproducir video"
                    >
                      {overlayImage && (
                        <img
                          src={overlayImage}
                          alt={selected.title}
                          className="vv-videoOverlayImg"
                        />
                      )}
                      <div className="vv-videoOverlayGradient" />
                      <div className="vv-videoOverlayContent">
                        <div className="vv-overlayPlayIcon">▶</div>
                        <div className="vv-overlayPlayText">
                          Reproducir video
                        </div>
                        <div className="vv-overlaySubtitle">
                          Haz clic para ver la clase sin distracciones ni
                          botones de YouTube.
                        </div>
                      </div>
                    </button>
                  )}
                  {/* Escudo para bloquear clics sobre el logo / título de YouTube */}
                  {isPlaying && <div className="vv-clickShield" />}
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                }}
              >
                <p style={{ margin: 0, color: "#475569" }}>
                  Agrega el <strong>youtubeId</strong> para {selected?.title} en{" "}
                  <code>classesData</code>.
                </p>
              </div>
            )
          ) : (
            <section className="vv-materials">
              <h2 className="vv-materialsTitle">Guía / material de estudio</h2>
              {selected?.materials && selected.materials.length > 0 ? (
                <ul className="vv-materialsList">
                  {selected.materials.map((m, idx) => (
                    <li key={idx}>
                      <button
                        className="vv-materialItemBtn"
                        type="button"
                        onClick={() => handleMaterialClick(m.to)}
                      >
                        <span className="vv-materialBullet" />
                        <span>{m.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p
                  style={{ margin: 0, fontSize: 13, color: "#6b7280" }}
                >
                  Aún no hay material de estudio cargado para esta clase.
                </p>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
